import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { giphyBlack, giphyDarkestGrey, giphyYellow } from '@giphy/js-brand'
import { uniqueId } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { Range } from 'types'
import { RangeSliderProps } from 'ui/src/components/controls/range-slider'
import { useRefDimensions } from 'utils'
import useMediaInfo from '../../hooks/use-media-info'
import { Container, RangeSlider, Strip } from './style'

interface Props extends Omit<RangeSliderProps, 'defaultRange' | 'gradient' | 'range' | 'onChange'> {
    defaultTiming?: Range
    duration?: number
    loops?: number
    reversed?: boolean
    source: File | string
    timing?: Range
    onChange?: (timing: Range) => void
}

export const MediaTrimmerWithoutContext = ({
    backgroundColor = giphyDarkestGrey,
    children,
    className,
    color = giphyYellow,
    defaultTiming,
    disabled: disabledProp = false,
    dndId,
    duration: durationProp,
    handleColor = giphyBlack,
    loops = 1,
    size = 'medium',
    reversed,
    source,
    style,
    timing: timingProp,
    onChange,
    ...rangeSliderProps
}: Props) => {
    const idRef = useRef<string>(dndId || uniqueId('media-trimmer'))
    const initialValueRef = useRef<Range | undefined>(undefined)
    const containerRef = useRef<HTMLDivElement>(null)
    const [range, setRange] = useState<Range | undefined>()
    const dimensions = useRefDimensions(containerRef)
    const mediaInfo = useMediaInfo(source)

    const duration = durationProp || mediaInfo?.duration || 0
    const disabled = disabledProp || !duration

    useEffect(() => {
        if (!duration) return

        const timing = timingProp || defaultTiming || { start: 0, end: duration }

        initialValueRef.current = { start: timing.start / duration, end: timing.end / duration }
        setRange(initialValueRef.current)
    }, [duration, timingProp])

    useEffect(() => {
        if (!range || !duration || disabled) return

        if (range.start !== initialValueRef.current?.start || range.end !== initialValueRef.current?.end) {
            initialValueRef.current = undefined

            onChange?.({
                start: Math.round(range.start * duration * 100) / 100,
                end: Math.round(range.end * duration * 100) / 100,
            })
        }
    }, [range?.end, range?.start])

    return (
        <Container
            backgroundColor={backgroundColor}
            className={className}
            controlSize={size}
            disabled={disabled}
            ref={containerRef}
            style={style}
        >
            {mediaInfo && dimensions && range && (
                <>
                    <Strip
                        backgroundColor={backgroundColor}
                        dimensions={dimensions}
                        dndId={idRef.current}
                        loops={loops}
                        mediaInfo={mediaInfo}
                        range={range}
                        reversed={reversed}
                        source={source}
                    />
                    {duration > 0 && (
                        <RangeSlider
                            {...rangeSliderProps}
                            backgroundColor="transparent"
                            color={color}
                            dndId={idRef.current}
                            handleColor={handleColor}
                            range={range}
                            size={size}
                            onChange={setRange}
                        >
                            {children}
                        </RangeSlider>
                    )}
                </>
            )}
        </Container>
    )
}

const MediaTrimmer = (props: Props) => {
    const pointerSensor = useSensor(PointerSensor)
    const sensors = useSensors(pointerSensor)

    return (
        <DndContext sensors={sensors}>
            <MediaTrimmerWithoutContext {...props} />
        </DndContext>
    )
}

export default MediaTrimmer
